import { useEffect, useState } from 'react';
import Router, { NextRouter } from 'next/router';
import NProgress from 'nprogress';
import { CORAL_THREAD_ID, CORAL_URL } from 'constants/index';
import { TenantEnv } from '../types';

export const useRouterEffect = (
  router: NextRouter,
  globalData: TenantEnv,
  restorePositionWhenBack = false,
) => {
  const [scrollPos, setScrollPos] = useState<{
    [key: string]: { x: number; y: number };
  }>({});
  const addScrollPos = (url: string) => {
    const newScrollPos = { x: window.scrollX, y: window.scrollY };
    setScrollPos((prev) => {
      return {
        ...prev,
        [url]: newScrollPos,
      };
    });
  };
  const restoreScrollPos = (url: string) => {
    if (scrollPos[url]) {
      window.scrollTo(scrollPos[url].x, scrollPos[url].y);
    }
  };
  useEffect(() => {
    Router.events.on('routeChangeStart', () => {
      NProgress.start();
    });
    Router.events.on('routeChangeComplete', () => NProgress.done());
    Router.events.on('routeChangeError', () => NProgress.done());
  }, []);

  useEffect(() => {
    if ('scrollRestoration' in window.history && restorePositionWhenBack) {
      let shouldScrollRestore = false;
      window.history.scrollRestoration = 'manual';
      restoreScrollPos(router.asPath);

      const onRouteChangeStart = () => {
        addScrollPos(router.asPath);
      };
      const onRouteChangeComplete = (url) => {
        if (shouldScrollRestore) {
          shouldScrollRestore = false;
          restoreScrollPos(url);
        }
      };

      Router.events.on('routeChangeStart', onRouteChangeStart);
      Router.events.on('routeChangeComplete', onRouteChangeComplete);
      Router.beforePopState(() => {
        shouldScrollRestore = true;
        return true;
      });

      return () => {
        Router.events.off('routeChangeStart', onRouteChangeStart);
        Router.events.off('routeChangeComplete', onRouteChangeComplete);
        Router.beforePopState(() => true);
      };
    }
  }, [router]);

  const reinitializeCoralScript = () => {
    // allow to add comment and receive reply
    if (globalData?.allowCommentAndReceiveReply) {
      // check if coral elements exist
      const coralEle = document.getElementById('coral_id');
      if (coralEle) {
        document.body.removeChild(coralEle);
      }
      const temp = `script[src="${CORAL_URL}/assets/js/embed.js"]`;
      const coralScriptNodes = document.head.querySelectorAll(temp);
      const len = coralScriptNodes.length;
      for (let i = 0; i < len; ++i) {
        coralScriptNodes[i].parentNode.removeChild(coralScriptNodes[i]);
      }

      const script = document.createElement('script');
      script.id = 'coral_id';
      script.innerHTML = `
        (function() {
          var d = document, s = d.createElement('script');
          s.src = '${CORAL_URL}' + '/assets/js/embed.js';
          s.async = false;
          s.defer = true;
          s.onload = function() {
            var coralEle = document.getElementById('${CORAL_THREAD_ID}');
            if (coralEle) {                      
              Coral.createStreamEmbed({
                id: '${CORAL_THREAD_ID}',
                autoRender: true,
                rootURL: '${CORAL_URL}',
                storyURL: window.location.href,
                customCSSURL: window.location.protocol + '//' + window.location.host + '/${globalData.tenant}/coral.css',
                bodyClassName: 'comment-body',
              })
            }
          };
          (d.head || d.body).appendChild(s);
        })();
      `;
      document.body.appendChild(script);
    }
  };

  useEffect(() => {
    reinitializeCoralScript();
  }, []);

  useEffect(() => {
    Router.events.on('routeChangeComplete', reinitializeCoralScript);
  }, [router]);
};
